/**
 * @engine/vfx - System Extractor
 * 
 * Extracts VFX systems from a graph by following contextFlowEdges.
 * 
 * Each context chain becomes a separate system:
 * - Chains starting with Spawn context → normal spawn system
 * - Chains starting with Event context → event-driven spawn system
 */

import type { ContextInstance } from '@engine/graph';
import type { VFXGraph } from '../VFXGraph';
import type { VFXNodeInstance, VFXContextFlowEdge } from '../types';
import type {
  VFXExtractedSystem,
  VFXSpawnConfig,
  VFXEventLink,
  VFXCompilationError,
} from './types';
import { vfxNodeRegistry } from '../VFXNodeRegistry';
import { VFX_MAX_PARTICLES } from '../VFXBindings';

// ============================================================================
// SYSTEM EXTRACTOR
// ============================================================================

/**
 * Result of system extraction.
 */
export interface ExtractionResult {
  /** Extracted systems */
  systems: VFXExtractedSystem[];
  
  /** Extraction errors */
  errors: VFXCompilationError[];
  
  /** Warnings */
  warnings: string[];
}

/**
 * A context chain discovered in the graph.
 */
interface ContextChain {
  /** Chain ID (from root context) */
  id: string;
  
  /** Root context type ('spawn' or 'event') */
  rootType: 'spawn' | 'event';
  
  /** Ordered list of contexts in this chain */
  contexts: ContextInstance[];
  
  /** For event chains: the trigger node that feeds this chain */
  triggerNode?: VFXNodeInstance;
  
  /** For event chains: the event ID */
  eventId?: number;
}

/**
 * Extract all VFX systems from a graph.
 * 
 * Discovers context chains by following contextFlowEdges.
 * Each chain becomes a VFXExtractedSystem.
 */
export function extractSystems(graph: VFXGraph): ExtractionResult {
  const errors: VFXCompilationError[] = [];
  const warnings: string[] = [];
  const systems: VFXExtractedSystem[] = [];
  
  // Discover all context chains
  const chains = discoverChains(graph, errors);
  // Convert each chain to an extracted system
  let eventIdCounter = 0;
  for (const chain of chains) {
    const system = extractSystemFromChain(chain, graph, errors, warnings, eventIdCounter);
    if (system) {
      systems.push(system);
      if (chain.rootType === 'event') {
        eventIdCounter++;
      }
    }
  }
  
  // Link events: find trigger nodes in spawn systems that connect to event systems
  linkEventSystems(systems, chains, graph);
  
  return { systems, errors, warnings };
}

/**
 * Discover all context chains in the graph.
 * A chain is a sequence of connected contexts.
 */
function discoverChains(graph: VFXGraph, errors: VFXCompilationError[]): ContextChain[] {
  const chains: ContextChain[] = [];
  const contexts = graph.contexts;
  const flowEdges = graph.contextFlowEdges;
  
  // Build lookup maps
  const contextById = new Map<string, ContextInstance>();
  for (const ctx of contexts) {
    contextById.set(ctx.id, ctx);
  }
  
  // Build downstream map (contextId → downstream contextId)
  const downstreamMap = new Map<string, string>();
  const upstreamSet = new Set<string>();
  for (const edge of flowEdges) {
    downstreamMap.set(edge.fromContextId, edge.toContextId);
    upstreamSet.add(edge.toContextId);
  }
  
  // Find root contexts (spawn or event type, or no upstream)
  const rootContexts: ContextInstance[] = [];
  for (const ctx of contexts) {
    const ctxType = ctx.type.replace('vfx.', '');
    if (ctxType === 'spawn' || ctxType === 'event') {
      rootContexts.push(ctx);
    }
  }
  
  // Walk from each root to build chains
  for (const root of rootContexts) {
    const chain = walkChain(root, downstreamMap, contextById);
    const rootType = root.type.replace('vfx.', '') as 'spawn' | 'event';
    
    chains.push({
      id: root.id,
      rootType: rootType === 'event' ? 'event' : 'spawn',
      contexts: chain,
    });
  }
  
  // Validate: at least one spawn chain required
  const spawnChains = chains.filter(c => c.rootType === 'spawn');
  if (spawnChains.length === 0) {
    errors.push({
      code: 'NO_SPAWN',
      message: 'No Spawn context found. VFX requires at least one Spawn.',
    });
  }
  
  return chains;
}

/**
 * Walk from a root context following flow edges.
 */
function walkChain(
  root: ContextInstance,
  downstreamMap: Map<string, string>,
  contextById: Map<string, ContextInstance>
): ContextInstance[] {
  const chain: ContextInstance[] = [root];
  let currentId = root.id;
  
  while (downstreamMap.has(currentId)) {
    const nextId = downstreamMap.get(currentId)!;
    const nextCtx = contextById.get(nextId);
    if (!nextCtx) break;
    chain.push(nextCtx);
    currentId = nextId;
  }
  
  return chain;
}

/**
 * Extract a VFXExtractedSystem from a context chain.
 */
function extractSystemFromChain(
  chain: ContextChain,
  graph: VFXGraph,
  errors: VFXCompilationError[],
  warnings: string[],
  eventId: number
): VFXExtractedSystem | null {
  // Find contexts by type in chain
  const findContext = (type: string) => 
    chain.contexts.find(c => c.type === type || c.type === `vfx.${type}`);
  
  const spawnCtx = findContext('spawn');
  const eventCtx = findContext('event');
  const initCtx = findContext('initialize');
  const updateCtx = findContext('update');
  const outputCtx = findContext('output');
  
  // Event chain must have event context, spawn chain must have spawn context
  if (chain.rootType === 'spawn' && !spawnCtx) {
    errors.push({
      code: 'NO_SPAWN',
      message: `Chain ${chain.id} is missing Spawn context.`,
    });
    return null;
  }
  
  if (chain.rootType === 'event' && !eventCtx) {
    errors.push({
      code: 'INVALID_NODE',
      message: `Event chain ${chain.id} is missing Event context.`,
    });
    return null;
  }
  
  // Get nodes from contexts
  const spawnNodes = spawnCtx ? graph.getNodesInContext(spawnCtx.id) : [];
  const eventNodes = eventCtx ? graph.getNodesInContext(eventCtx.id) : [];
  const initNodes = initCtx ? graph.getNodesInContext(initCtx.id) : [];
  const updateNodes = updateCtx ? graph.getNodesInContext(updateCtx.id) : [];
  const outputNodes = outputCtx ? graph.getNodesInContext(outputCtx.id) : [];
  
  // Validate Initialize has nodes
  if (initNodes.length === 0) {
    warnings.push(`Chain ${chain.id}: Initialize context is empty.`);
  }
  
  // Extract spawn configuration
  let spawnConfig: VFXSpawnConfig;
  if (chain.rootType === 'event') {
    // Event-driven spawn
    spawnConfig = {
      mode: 'event',
      eventId,
      eventRatio: 1.0,
    };
  } else {
    spawnConfig = extractSpawnConfig(spawnNodes, errors, spawnCtx?.id);
  }
  
  // Check for output event (On Die trigger in update nodes)
  const outputEvent = extractOutputEvent(updateNodes, graph.getEdges(), graph);
  
  // Get max particles and validate against WebGPU limit
  const maxParticles = initCtx ? graph.getContextCapacity(initCtx.id) : 10000;
  
  if (maxParticles > VFX_MAX_PARTICLES) {
    errors.push({
      code: 'INVALID_CAPACITY',
      message: `System "${chain.id}" has ${maxParticles.toLocaleString()} particles which exceeds the WebGPU limit of ${VFX_MAX_PARTICLES.toLocaleString()}. Use multiple systems for higher particle counts.`,
      contextId: initCtx?.id,
    });
    return null;
  }
  
  // Build the extracted system
  const systemName = chain.rootType === 'spawn' ? 'Main System' : `Event System ${eventId}`;
  
  const system: VFXExtractedSystem = {
    id: chain.id,
    name: systemName,
    maxParticles,
    spawn: {
      contextId: spawnCtx?.id ?? eventCtx?.id ?? '',
      nodes: spawnNodes,
      config: spawnConfig,
    },
    initialize: {
      contextId: initCtx?.id ?? '',
      nodes: initNodes,
    },
    update: {
      contextId: updateCtx?.id ?? '',
      nodes: updateNodes,
    },
    output: {
      contextId: outputCtx?.id ?? '',
      nodes: outputNodes,
    },
    event: eventCtx ? {
      contextId: eventCtx.id,
      nodes: eventNodes,
    } : undefined,
    inputEvent: chain.rootType === 'event' ? { eventId } : undefined,
    outputEvent,
  };
  
  return system;
}

/**
 * Link event systems by finding trigger nodes that connect to Event Transit nodes.
 */
function linkEventSystems(
  systems: VFXExtractedSystem[],
  chains: ContextChain[],
  graph: VFXGraph
): void {
  const edges = graph.getEdges();
  
  // Find all Event Transit nodes and their connected triggers
  for (const chain of chains) {
    if (chain.rootType !== 'event') continue;
    
    const eventCtx = chain.contexts.find(c => 
      c.type === 'event' || c.type === 'vfx.event'
    );
    if (!eventCtx) continue;
    
    const eventNodes = graph.getNodesInContext(eventCtx.id);
    const transitNode = eventNodes.find(n => n.type === 'vfx.event.transit');
    if (!transitNode) continue;
    
    // Find trigger node connected to this transit
    const triggerEdge = edges.find(e => 
      e.to.nodeId === transitNode.id && e.to.portId === 'event_in'
    );
    if (!triggerEdge) {
      continue;
    }
    
    // Find the trigger node
    const triggerNode = graph.getNode(triggerEdge.from.nodeId);
    if (!triggerNode) continue;
    
    // Find which spawn system contains this trigger node
    const spawnChain = chains.find(c => 
      c.rootType === 'spawn' && 
      c.contexts.some(ctx => ctx.nodeIds.includes(triggerNode.id))
    );
    
    if (spawnChain) {
      // Get spawn count from trigger node
      const spawnCount = (triggerNode.inputValues?.count as number) ?? 1;
      
      // Find the corresponding systems
      const spawnSystem = systems.find(s => s.id === spawnChain.id);
      const eventSystem = systems.find(s => s.id === chain.id);
      
      if (spawnSystem && eventSystem) {
        // Mark spawn system as having output event
        const eventId = eventSystem.inputEvent?.eventId ?? 0;
        (spawnSystem as any).outputEvent = {
          eventId,
          spawnCount,
          triggerType: triggerNode.type,
        };
        
        // Also store spawnCount in eventSystem.inputEvent for easy access
        if (eventSystem.inputEvent) {
          (eventSystem.inputEvent as any).spawnCount = spawnCount;
        }
      }
    }
  }
}

// ============================================================================
// SPAWN CONFIG EXTRACTION
// ============================================================================

/**
 * Extract spawn configuration from Spawn context nodes.
 */
function extractSpawnConfig(
  nodes: readonly VFXNodeInstance[],
  errors: VFXCompilationError[],
  contextId?: string
): VFXSpawnConfig {
  // Find spawn node (should be only one due to maxBlocks: 1)
  const spawnNode = nodes.find(n => 
    n.type.startsWith('vfx.spawn.') &&
    n.type !== 'vfx.spawn.on_event' // on_event is different
  );
  
  const eventNode = nodes.find(n => n.type === 'vfx.spawn.on_event');
  
  // No spawn node = warning
  if (!spawnNode && !eventNode) {
    errors.push({
      code: 'INVALID_SPAWN',
      message: 'Spawn context has no spawn source. Add a Constant Rate, Burst, or Event spawn node.',
      contextId,
    });
    return { mode: 'none' };
  }
  
  // Event-driven spawn
  if (eventNode) {
    return {
      mode: 'event',
      eventId: getNodeInputValue(eventNode, 'eventId', 0),
      eventRatio: getNodeInputValue(eventNode, 'ratio', 1.0),
    };
  }
  
  // Constant rate
  if (spawnNode!.type === 'vfx.spawn.rate') {
    return {
      mode: 'rate',
      rate: getNodeInputValue(spawnNode!, 'rate', 10),
    };
  }
  
  // Variable rate
  if (spawnNode!.type === 'vfx.spawn.variable_rate') {
    return {
      mode: 'variable_rate',
      variableRate: {
        rateMin: getNodeInputValue(spawnNode!, 'rateMin', 0),
        rateMax: getNodeInputValue(spawnNode!, 'rateMax', 100),
      },
    };
  }
  
  // Full burst (with delay, repeat, interval)
  if (spawnNode!.type === 'vfx.spawn.burst') {
    return {
      mode: 'burst',
      burst: {
        count: getNodeInputValue(spawnNode!, 'count', 10),
        delay: getNodeInputValue(spawnNode!, 'delay', 0),
        repeat: getNodeInputValue(spawnNode!, 'repeat', 1),
        interval: getNodeInputValue(spawnNode!, 'interval', 1),
      },
    };
  }
  
  // Single burst (one-shot, optional delay)
  if (spawnNode!.type === 'vfx.spawn.single') {
    return {
      mode: 'single_burst',
      burst: {
        count: getNodeInputValue(spawnNode!, 'count', 100),
        delay: getNodeInputValue(spawnNode!, 'delay', 0),
        repeat: 1,
        interval: 0,
      },
    };
  }
  
  // Periodic burst (infinite repeat at interval)
  if (spawnNode!.type === 'vfx.spawn.periodic') {
    return {
      mode: 'periodic_burst',
      burst: {
        count: getNodeInputValue(spawnNode!, 'count', 10),
        delay: getNodeInputValue(spawnNode!, 'delay', 0),
        repeat: -1, // infinite
        interval: getNodeInputValue(spawnNode!, 'period', 1),
      },
    };
  }
  
  // Unknown spawn type
  return { mode: 'none' };
}

/**
 * Extract output event from Update nodes.
 * Checks for:
 * 1. GPU Event node (legacy)
 * 2. Trigger nodes (On Die, etc.) connected to Event Transit (new style)
 */
function extractOutputEvent(
  nodes: readonly VFXNodeInstance[], 
  edges: readonly { from: { nodeId: string; portId: string }; to: { nodeId: string; portId: string } }[],
  graph: VFXGraph
): VFXEventLink | undefined {
  // Check for legacy GPU Event node
  const gpuEventNode = nodes.find(n => n.type === 'vfx.output.gpu_event');
  if (gpuEventNode) {
    const triggerEdge = edges.find(e => 
      e.to.nodeId === gpuEventNode.id && e.to.portId === 'trigger'
    );
    const triggerNode = triggerEdge ? nodes.find(n => n.id === triggerEdge.from.nodeId) : null;
    const spawnCount = triggerNode ? getNodeInputValue(triggerNode, 'count', 1) : 1;
    return {
      eventId: 0,
      spawnCount,
    };
  }
  
  // Check for trigger nodes connected to Event Transit (new style)
  const triggerNodes = nodes.filter(n => n.type.startsWith('vfx.trigger.'));
  for (const triggerNode of triggerNodes) {
    // Check if this trigger has an outgoing edge
    const outgoingEdge = edges.find(e => 
      e.from.nodeId === triggerNode.id && e.from.portId === 'event'
    );
    if (outgoingEdge) {
      // This trigger is connected to something (likely Event Transit)
      const spawnCount = getNodeInputValue(triggerNode, 'count', 1);
      return {
        eventId: 0, // Will be assigned by linkEventSystems
        spawnCount,
        triggerType: triggerNode.type,
      } as VFXEventLink & { triggerType?: string };
    }
  }
  
  return undefined;
}

/**
 * Get a node's input value with type conversion.
 */
function getNodeInputValue<T>(node: VFXNodeInstance, inputId: string, defaultValue: T): T {
  const value = node.inputValues[inputId];
  if (value === undefined || value === null) {
    return defaultValue;
  }
  
  if (typeof defaultValue === 'number') {
    return Number(value) as T;
  }
  
  return value as T;
}
